import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { createEvvDevHelpRequest } from "../evv-dev-help.api";

type Props = {
  isOpen: boolean;
  visitInstanceIds: number[];
  onClose: () => void;
  /** Called after the request is accepted so the caller can clear its selection and refresh. */
  onFlagged: () => void;
};

export function EvvDevHelpModal(props: Props) {
  const toast = useToast();
  const [reasonText, setReasonText] = React.useState("");

  // Reset on every open so a previous request's text never leaks into a new one.
  React.useEffect(() => {
    if (props.isOpen) {
      setReasonText("");
    }
  }, [props.isOpen]);

  const mutation = useMutation({
    mutationFn: () =>
      createEvvDevHelpRequest({
        visitInstanceIds: props.visitInstanceIds,
        reasonText: reasonText.trim(),
      }),
    onSuccess: (result) => {
      const flagged = result.flaggedVisitInstanceIds.length;
      const alreadyOpen = result.alreadyOpenVisitInstanceIds.length;
      toast({
        title: flagged > 0 ? "Sent to a developer" : "Nothing new to send",
        description: `${flagged} flagged, ${alreadyOpen} already open.`,
        status: flagged > 0 ? "success" : "info",
        position: "top-right",
        duration: 8000,
        isClosable: true,
      });
      props.onFlagged();
      props.onClose();
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not send the request",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        duration: 10000,
        isClosable: true,
      });
    },
  });

  const count = props.visitInstanceIds.length;

  return (
    <Modal isOpen={props.isOpen} size="lg" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Need dev help</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="gray.600" mb={4}>
            A developer will look at {count} selected visit{count === 1 ? "" : "s"} that will not
            submit. You will see the status, and their note, on this page once it is handled.
          </Text>
          <FormControl isRequired>
            <FormLabel>What is going wrong?</FormLabel>
            <Textarea
              placeholder="Describe the problem and anything you already tried"
              rows={5}
              value={reasonText}
              onChange={(event) => setReasonText(event.target.value)}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={reasonText.trim() === "" || count === 0}
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Send to developer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
